import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setIsLoading, setReportEmployeeEarningList } from "../../slices/reportSlice";
import { generateRandomString } from "utils/helper";
import * as services from "../../services/reportApi";
import useOptions from "../../../masterData/hooks/useOptions";
import dayjs from "dayjs"

function useEmployeePayrollReport() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const [search, setSearch] = useState({});
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const currentEmployee = useSelector((state) => state.company.currentEmployee);
    const reportEmployeeEarningTable = useSelector((state) => state.report.reportEmployeeEarningTable || []);
    const [showReport, setShowReport] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [positionOptions, setPositionOptions] = useState([]);
    const [periodOptions, setPeriodOptions] = useState([]);
    const [departmentListOption, setDepartmentListOption] = useState([]);
    const [pagination, setPagination] = useState({ page: 0, currentPage: 0, size: 10 });
    const [year, setYear] = useState(dayjs().year());
    const [month, setMonth] = useState();
    const [isExcelLoading, setIsExcelLoading] = useState(false);
    const [isExcelLoading2, setIsExcelLoading2] = useState(false);
    const [isPDFLoading, setIsPDFLoading] = useState(false);

    function toParams(params = {}) {
        const { pagination = {}, sortBy = [] } = params;
        const modifiedParams = {
            pagination: {
                page: pagination?.page,
                size: pagination?.pageSize,
            },
            sortBy: sortBy
                .filter((s) => s.order !== undefined)
                .map((s) => ({
                    direction: s.order === "descend" ? "desc" : "asc",
                    property: s.field,
                })),
        };
        return modifiedParams;
    }

    const { employeeStatusTypeOptions,
        employmentTypeOptions,
        getEmployeeStstuaTypeOptions,
        getEmploymentTypeOptions } = useOptions();

    const modifiedPositionOptions = useMemo(() => {
        const modifiedOptions = positionOptions.map((opt) => ({
            ...opt,
            id: opt?.positionId,
            value: opt?.positionId,
            nameEn: opt?.positionNameEn,
            name: opt?.positionName,
            label: i18n.language === "en" ? opt?.positionNameEn : opt?.positionName,
        }));
        return modifiedOptions?.sort((a, b) =>
            a?.label?.localeCompare(b?.label, i18n?.language === "en" ? "en" : "th", { sensitivity: "base" }));
    }, [i18n.language, positionOptions]);

    const modifiedDepartmentOption = useMemo(() => {
        const modifiedOptions = departmentListOption?.map((opt) => ({
            ...opt,
            value: opt?.id,
            label: i18n.language === "en" ? opt?.nameEn : opt?.name,
        }));

        return modifiedOptions?.sort((a, b) =>
            a?.label?.localeCompare(b?.label, i18n?.language === "en" ? "en" : "th", { sensitivity: "base" }));
    }, [i18n.language, departmentListOption]);

    const modifiedPeriodOption = useMemo(() => {
        const modifiedOptions = periodOptions?.map((opt) => ({
            ...opt,
            value: opt?.id,
            label: i18n.language === "en" ? opt?.nameEn : opt?.name,
        }));

        return modifiedOptions;
    }, [i18n.language, periodOptions]);

    const getPosition = useCallback(async () => {
        if (typeof currentCompany !== "object") {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                };
                const response = await services.getPosition(values);
                setPositionOptions(response.data);
            } catch (error) {
                // alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, dispatch]);

    const getDepartment = useCallback(async () => {
        if (!currentCompany) return null;
        try {
            dispatch(setIsLoading(true));
            const response = await services.getDepartmentOptions({
                requestId: generateRandomString(),
                companyId: currentCompany,
            });
            dispatch(setDepartmentListOption(response.data));
        } catch (error) {
            // alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, dispatch]);

    const updatePeriodOptions = useCallback(
        async (targetYear, targetMonth) => {
            try {
                dispatch(setIsLoading(true));
                if (targetYear && !targetMonth) {
                    const yearResponse = await services.getPeriodOptions({ year: targetYear });
                    dispatch(setPeriodOptions(yearResponse?.data));
                } else if (targetYear && targetMonth) {
                    console.log("currentCompany", currentCompany);

                    const monthResponse = await services.getPeriodOptions({ companyId: currentCompany, year: targetYear, month: targetMonth });
                    dispatch(setPeriodOptions(monthResponse?.data));
                }
            } catch (error) {
                console.error("Error updating dropdown options:", error);
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [dispatch]
    );

    const getEmployeeSalaryTable = useCallback(
        async (params = {}, changeYear) => {
            try {
                console.log("asdadsa", params?.year)
                dispatch(setIsLoading(true));
                const response = await services.getEmployeeEarningReport({
                    ...params,
                    ...params.pagination,
                    requestId: generateRandomString(),
                    employeeId: currentEmployee,
                    companyId: currentCompany,
                    informationType: 1,
                    year: changeYear || year,
                });

                dispatch(setReportEmployeeEarningList(response?.data?.page?.content));

                setPagination({
                    ...pagination,
                    current: response?.data?.page?.pageable?.pageNumber + 1,
                    pageNo: response?.data?.page?.pageable?.pageNumber + 1,
                    size: response?.data?.page?.pageable?.pageSize,
                    total: response?.data?.page?.totalElements,
                });
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, dispatch, pagination, year]
    );

    const exportEmployeeEarningRecordReportExcel = useCallback(
        async (params = {}, changeYear) => {
            setIsExcelLoading(true);
            try {
                dispatch(setIsLoading(true));
                const res = await services.ExportEmployeeEarningRecordReportExcel({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    year: changeYear || year,
                });
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "employee-earning-record-report.xlsx");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsExcelLoading(false);
            }
        },
        [currentCompany, dispatch, search, year]
    );
    const exportEmployeeEarningRecordReportExcelPlus = useCallback(
        async (params = {}, changeYear) => {
            setIsExcelLoading2(true);
            try {
                dispatch(setIsLoading(true));
                const res = await services.ExportEmployeeEarningRecordReportExcelPlus({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    year: changeYear || year,
                });
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "payroll_period_report.xlsx");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsExcelLoading2(false);
            }
        },
        [currentCompany, dispatch, search, year]
    );

    const exportEmployeeEarningRecordReportPdf = useCallback(
        async (params = {}, changeYear) => {
            setIsPDFLoading(true);
            try {
                dispatch(setIsLoading(true));
                const res = await services.ExportEmployeeEarningRecordReportPdf({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    year: changeYear || year,
                })
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "employee-earning-record-report.pdf");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsPDFLoading(false);
            }
        },
        [currentCompany, dispatch, search]
    );

    const exportEmployeeEarningRecordReportPdfByEmp = useCallback(
        async (params = {}, changeYear) => {
            try {
                dispatch(setIsLoading(true));
                services.ExportEmployeeEarningRecordReportPdf({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    year: changeYear || year,
                }).then(res => {
                    const url = window.URL.createObjectURL(res?.data);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', "employee-earning-record-report-by-emp.pdf");
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                });
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, dispatch, search]
    );

    const previewEmployeeEarningReport = useCallback(async (params = {}, changeYear) => {
        try {
            dispatch(setIsLoading(true));

            const response = await services.ExportEmployeeEarningRecordReportPdf({
                requestId: generateRandomString(),
                companyId: currentCompany,
                ...params,
                ...params.pagination,
                ...search,
                ...pagination,
                isReport: "Y",
                sortBy: params.sortBy,
                year: changeYear || year,
            })

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            const modal = document.createElement('div');
            modal.style.position = 'fixed';
            modal.style.top = '0';
            modal.style.left = '0';
            modal.style.width = '100%';
            modal.style.height = '100%';
            modal.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
            modal.style.display = 'flex';
            modal.style.alignItems = 'center';
            modal.style.justifyContent = 'center';
            modal.style.zIndex = '9999';

            const iframe = document.createElement('iframe');
            iframe.style.width = '80%';
            iframe.style.height = '80vh';
            iframe.src = url;

            const closeButton = document.createElement('button');
            closeButton.textContent = t("common.close");
            closeButton.style.position = 'absolute';
            closeButton.style.top = '20px';
            closeButton.style.right = '20px';
            closeButton.style.padding = '10px 20px';
            closeButton.style.backgroundColor = '#ff0000';
            closeButton.style.color = '#fff';
            closeButton.style.border = 'none';
            closeButton.style.borderRadius = '5px';
            closeButton.style.cursor = 'pointer';
            closeButton.style.fontSize = '16px';

            closeButton.addEventListener('click', () => {
                document.body.removeChild(modal);
                window.URL.revokeObjectURL(url);
            });

            modal.appendChild(iframe);
            modal.appendChild(closeButton);

            document.body.appendChild(modal);

        } catch (error) {
            alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [])

    const onChange = useCallback(
        (tablePagination, tableFilters, tableSorter, tableExtra) => {
            const modifiedSorter = Array.isArray(tableSorter)
                ? [...tableSorter]
                : [tableSorter];
            const modifiedParams = toParams({
                pagination: {
                    page: tablePagination.current - 1,
                    pageSize: tablePagination.pageSize,
                    current: tablePagination.current - 1,
                },
                sortBy: modifiedSorter,
            });

            setPagination({ ...pagination, ...modifiedParams?.pagination })
            getEmployeeSalaryTable({ ...modifiedParams, request: pagination.request, changeYear: year, ...search });
        },
        [getEmployeeSalaryTable, pagination, search]
    );

    useEffect(() => {
        if (currentCompany) {
            getPosition();
            getDepartment();
            getEmployeeStstuaTypeOptions();
            getEmploymentTypeOptions({ companyId: currentCompany });
        }
    }, [currentCompany, getPosition, getDepartment, getEmployeeStstuaTypeOptions, getEmploymentTypeOptions])

    useEffect(() => {
        if (year) {
            updatePeriodOptions(year, month);
        }
    }, [year, month, updatePeriodOptions]);

    const showModal = () => {
        setIsModalOpen(true);
    };

    const handleSearch = (values) => {
        setShowReport(true)
        setSearch({})
        setSearch({ ...values })
        getEmployeeSalaryTable({ ...values, ...pagination, year: values?.year?.format("YYYY"), month: values?.month?.format("MM") })
    };

    return {
        showReport,
        isModalOpen,
        positionOptions: modifiedPositionOptions,
        departmentOptions: modifiedDepartmentOption,
        employeeStatusTypeOptions,
        employmentTypeOptions,
        periodOptions: modifiedPeriodOption,
        pagination,
        reportEmployeeEarningTable,
        isExcelLoading,
        isPDFLoading,
        isExcelLoading2,
        handleSearch,
        showModal,
        setIsModalOpen,
        onChange,
        updatePeriodOptions,
        setYear,
        setMonth,
        exportEmployeeEarningRecordReportExcel,
        exportEmployeeEarningRecordReportExcelPlus,
        exportEmployeeEarningRecordReportPdf,
        previewEmployeeEarningReport,
        exportEmployeeEarningRecordReportPdfByEmp
    }
}

export default useEmployeePayrollReport;